/**
 * The small pure rules of the AI generation screens (document/64): what the
 * dialogs offer and how they read what the gateway answers. Kept out of the
 * components so they can be tested without a browser.
 */
import type { AiAspectRatio, AiJobView, AiProposedSection } from '@cms/contracts'

/** The formats the generators accept, widest first. The label of each is in the dictionary (aiGen.aspect.*). */
export const ASPECT_OPTIONS: readonly AiAspectRatio[] = ['16:9', '4:3', '1:1', '3:4', '9:16']

export const PROMPT_MIN = 3
export const PROMPT_MAX = 2000
export const CLIP_MAX_SECONDS = 10
export const CLIP_DEFAULT_SECONDS = 6

export type PromptProblem = 'short' | 'long' | null

export function promptProblem(prompt: string): PromptProblem {
  const length = prompt.trim().length
  if (length < PROMPT_MIN) return 'short'
  return length > PROMPT_MAX ? 'long' : null
}

/** The wait before the next question to the gateway: what it asked, never less than 2 s, never more than a minute. */
export function nextPollDelayMs(job: Pick<AiJobView, 'pollAfterSeconds'>): number | null {
  if (job.pollAfterSeconds === null) return null
  return Math.min(60, Math.max(2, job.pollAfterSeconds)) * 1000
}

export function jobIsOver(job: Pick<AiJobView, 'status' | 'posterPending'>): boolean {
  return (job.status === 'ready' && !job.posterPending) || job.status === 'failed'
}

/** The chip tone of a job, in the status vocabulary of the rest of the interface. */
export function jobTone(status: AiJobView['status']): 'progress' | 'success' | 'error' | 'neutral' {
  switch (status) {
    case 'running': return 'progress'
    case 'ready': return 'success'
    case 'failed': return 'error'
    default: return 'neutral'
  }
}

/** One line to recognise a proposed section by: its title, question or first words. */
export function sectionHeadline(section: Pick<AiProposedSection, 'props'>): string {
  const props = section.props
  for (const key of ['title', 'question']) {
    const value = props[key]
    if (typeof value === 'string' && value.trim()) return value.trim()
  }
  const first = Array.isArray(props.items) ? props.items[0] : Array.isArray(props.steps) ? props.steps[0] : null
  if (first && typeof first === 'object') {
    const record = first as Record<string, unknown>
    const value = record.title ?? record.question ?? record.label
    if (typeof value === 'string') return value
  }
  const body = props.body ?? props.text ?? props.subtitle
  return typeof body === 'string' ? body.slice(0, 80) : ''
}

/** A short plain-text digest of a proposed section for the review list. */
export function sectionDigest(section: Pick<AiProposedSection, 'props'>): string[] {
  const lines: string[] = []
  const props = section.props
  for (const key of ['subtitle', 'text', 'body']) {
    const value = props[key]
    if (typeof value === 'string' && value.trim()) lines.push(value.trim().replace(/\s+/g, ' ').slice(0, 220))
  }
  for (const key of ['items', 'steps', 'actions']) {
    const list = props[key]
    if (!Array.isArray(list)) continue
    for (const entry of list.slice(0, 4)) {
      if (!entry || typeof entry !== 'object') continue
      const record = entry as Record<string, unknown>
      const label = record.title ?? record.question ?? record.text ?? record.label ?? record.value
      const detail = record.description ?? record.answer ?? record.href
      if (typeof label === 'string') lines.push(typeof detail === 'string' ? `${label}: ${detail}`.slice(0, 160) : label)
    }
  }
  return lines.slice(0, 5)
}

/** Replace one proposed section by id, keeping the order; an unknown id leaves the list as it is. */
export function replaceProposed(list: readonly AiProposedSection[], id: string, next: AiProposedSection): AiProposedSection[] {
  return list.map((entry) => (entry.id === id ? next : entry))
}

export function withoutProposed(list: readonly AiProposedSection[], id: string): AiProposedSection[] {
  return list.filter((entry) => entry.id !== id)
}

/**
 * The accounts a dropdown shows: usable ones first, the others kept but
 * disabled with the reason AI gave (document/42 §3.1: never hidden).
 */
export function accountOptions(accounts: ReadonlyArray<{ id: string; label: string; status?: string; reason?: string }>) {
  return accounts.map((account) => ({
    ...account,
    disabled: account.status !== undefined && account.status !== 'ready',
  }))
}
